// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFormField.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import TinyMCEConstants from 'logic/data/Izenda.TinyMCEConstants';
import ReportPartFormUtils from 'logic/models/Izenda.ReportPartFormUtils';
import UIContants from 'logic/data/Izenda.UIConstants';
import ReportDesignerConstants from 'logic/data/Izenda.ReportDesignerConstants';
import embededReportUtils from 'share/utils/Izenda.EmbededReportUtils';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ObjectUtils from 'share/utils/Izenda.ObjectUtils';
import SubReportUtils from 'share/utils/Izenda.SubReportUtils';
import AppGlobal from 'Izenda.AppGlobal';
import typeof ReportDesignerDataTree from 'logic/models/Izenda.ReportDesignerDataTree';
import typeof ReportPartElement from 'logic/models/Izenda.ReportPartElement';
import typeof ReportPartDetails from 'logic/models/Izenda.ReportPartDetails';
// $FlowFixMe
import { map, isNil, getOr, omit, get, trim, intersectionBy, isBoolean, find, mapKeys, isEmpty } from 'lodash/fp';

export default class ReportPartFormField {
  showValue(value: any): string {
    return String.commonStringValue(value, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);
  }

  /**
   * Generate field html
   * @param {Object} field mapping list
   * @param {Object} records
   * @param {Object} properties
   * @param {Object} data tree
   * @param {Object} visible elements
   * @param {String} html content
   * @param {String} report Id
   * @returns {Object} { htmlContent, embeddedSubReports }
   */
  generateField(
    props: any,
    fieldsMapping: Array<any>,
    records: Array<any>,
    properties: any,
    dataTree: ReportDesignerDataTree,
    visibleElement: Array<ReportPartElement>,
    newHtmlContent: string,
    reportId: string,
    filterForSubReport: any
  ) {

    const listFields = $izenda('<div>' + newHtmlContent + '</div>').find('field');
    let embeddedSubReports = [];
    const { printingMode, reportPart, viewMode } = props;

    if (!isEmpty(listFields)) {
      let result = '';
      const cachedData = {};
      const pagebreakHtml = TinyMCEConstants.PAGE_BREAK_HTML;
      const fieldNameAliases = buildListOfFieldNameAlias(listFields);
      const validationContext = {
        dataTree,
        fieldNameAliases
      };

      listFields.each((index, item) => {
        // Get column name from fields mapping and field info from elements to config
        const textContent = trim(item.textContent);
        const fieldMapping = find({ fieldNameAlias: textContent }, fieldsMapping) || {};
        const fieldInfo = find({ fieldName: textContent }, visibleElement) || {};

        let detail = null;
        if (!get('isVisible', fieldInfo)) {
          detail = {
            invisible: true,
            index
          };
        } else {
          const validationResult = this.validateField(item, validationContext);

          if (!printingMode && !viewMode && validationResult) {
            detail = {
              error: validationResult,
              index
            };
          } else {
            detail = {
              value: fieldMapping.columnName,
              fieldInfo,
              index
            };
          }
        }

        cachedData[item.outerHTML] = detail;
      });

      records.forEach((record, index) => {
        let newContent = newHtmlContent;
        const domContent = $izenda('<div>' + newHtmlContent + '</div>');
        const listFields = domContent.find('field');

        mapKeys(key => {
          let newText = cachedData[key].error;
          const fieldInfo = cachedData[key].fieldInfo;

          if (!get('invisible', cachedData[key]) && isNil(newText) && fieldInfo.properties) {
            const columnName = cachedData[key].value;

            const mapValue = ReportPartUtils.getFieldValue(columnName, record);
            mapValue.value = this.showValue(mapValue.value);

            const indexHtml = newHtmlContent.indexOf(key);

            const data = this.formatField(
              {
                reportPartElm: fieldInfo,
                columnName
              },
              mapValue,
              reportId,
              index,
              reportPart,
              record,
              fieldsMapping,
              filterForSubReport,
              indexHtml
            );
            newText = data.newText;
            if (!isEmpty(data.embeddedSubReports)) {
              embeddedSubReports = embeddedSubReports.concat(data.embeddedSubReports);
            }
          }

          $izenda(listFields.get(cachedData[key].index)).replaceWith(newText);
          newContent = domContent.html();
        }, cachedData);

        // Page Break After Each Entry
        if (properties && properties.pageBreakAfterEachEntry && props.printingMode) {
          newContent = PrintingUtils.replaceFormStyles(props, newContent);
          newContent += pagebreakHtml;
        }

        result += newContent;
      });

      // Handle if haven't records return, so only replace field element with ''
      if (isEmpty(records)) {
        result = newHtmlContent;
        mapKeys(key => {
          result = result.replace(key, '');
        }, cachedData);
      }

      return {
        htmlContent: result,
        embeddedSubReports
      };
    }
    return {
      htmlContent: newHtmlContent,
      embeddedSubReports
    };

    /**
     * Build list of field name alias.
     * @param  {Array} listFields
     */
    function buildListOfFieldNameAlias(listFields) {
      return map(field => trim($izenda(field).text()), listFields);
    }
  }
  /**
   * Format fields
   * @param {Object} field information
   * @param {Object} value
   * @param {String} report id
   * @param {Number} index
   * @param {Object} report part
   * @param {Object} record data
   * @returns {Object} fields
   */
  formatField(
    field: any,
    mapValue: any,
    reportId: string,
    index: number,
    reportPart: ReportPartDetails,
    record: any,
    fieldsMapping: Array<any>,
    filterForSubReport: any,
    indexHtml: number
  ) {
    const fieldInfo = field.reportPartElm;
    const embeddedSubReports = [];
    const fieldInfoProperties = fieldInfo.properties;
    const dataFormattings = fieldInfoProperties.dataFormattings;
    const dataType = fieldInfo.previewDataType;

    const subReportConfig = getOr({}, 'drillDown.subReport', fieldInfoProperties);

    let newText = this.getTextWithFormat(dataFormattings, mapValue, dataType, field, record);

    if (
      subReportConfig.selectedReport &&
      subReportConfig.style === ReportDesignerConstants.DRILL_DOWN_STYLE_OPTION_VALUES.embedded
    ) {
      const subReportFilter = this.getSubReportQuery(
        fieldInfo,
        record,
        fieldsMapping,
        reportId,
        reportPart,
        filterForSubReport
      );
      // CustomURLLink and EmbededJavasript link/icon
      let divText = this.applySubReportEmbedded(newText, fieldInfo, index);
      if (isBoolean(subReportFilter.success) && !subReportFilter.success) {
        divText = divText + `<div>${subReportFilter.errors}</div>`;
      } else {
        const listEmbededReportPart = embededReportUtils.readEmbededReportDef(reportPart, fieldInfo);

        listEmbededReportPart.forEach(reportPartDef => {
          const reportPartObject = this.getSubReportObject(
            fieldInfo,
            index,
            reportPartDef.id,
            omit(['success'], subReportFilter),
            reportPartDef
          );
          divText = divText + `<div class="u_fullWidth ${reportPartObject.reportPartDomId}"></div>`;
          reportPartObject.index = indexHtml;
          embeddedSubReports.push(reportPartObject);
        });
      }

      newText = divText;
    } else {
      newText = this.applyHyperLink(newText, fieldInfo, index);
    }

    return {
      newText,
      embeddedSubReports
    };
  }

  /**
   * Get text with format
   * @param {Object} data formatting list
   * @param {Object} mapValue
   * {
   *  originalValue:
      value:
     }
   * @param {String} data type
   * @returns {String} text with format
   */
  getTextWithFormat(dataFormattings: any, mapValue: any, dataType: string, field: any, record: any) {
    const font = dataFormattings.font;
    const fieldInfo = field.reportPartElm;
    const { originalValue, value } = mapValue;
    if (ObjectUtils.isImage(fieldInfo.field.dataFieldType)) {
      return String.format('<img src=\'{0}\' />', value);
    }

    const formatSource = {
      element: field,
      record
    };
    const textColor = ReportPartUtils.getConfigDataByRange(
      originalValue,
      dataFormattings.color.textColor,
      formatSource
    );
    const backgroundColor = ReportPartUtils.getConfigDataByRange(
      originalValue,
      dataFormattings.color.cellColor,
      formatSource
    );
    let styles = '';

    if (font.family !== UIContants.defaultFontFamily) {
      styles += String.format('font-family:{0};', font.family);
    }

    if (font.size !== UIContants.defaultFontSize) {
      styles += String.format('font-size:{0}px;', font.size);
    }

    styles += font.bold ? 'font-weight:bold;' : '';
    styles += font.italic ? 'font-style:italic;' : '';
    styles += font.underline ? 'text-decoration:underline;' : '';
    styles += textColor ? `color:${textColor};` : '';
    styles += backgroundColor ? String.format('background-color:{0};', backgroundColor) : '';

    const valueFormated = ReportPartUtils.getDataFormat(
      mapValue,
      {
        alternativeText: dataFormattings.alternativeText,
        fieldFormatData: dataFormattings.format.format,
        fieldDataType: dataType
      },
      formatSource
    );

    return `<span style="${styles}">${valueFormated}</span>`;
  }

  applySubReportEmbedded(originalText: string, fieldInfo: any, index: number) {
    const fieldInfoProperties = fieldInfo.properties;

    const customURLAttribute = `data-selectedfieldname="${encodeURI(
      JSON.stringify(fieldInfo.fieldName)
    )}" data-recordIndex="${index}" data-hyperlinkaction="true" data-isCustomURL="true"`;
    const customURL = get('customURL', fieldInfoProperties);
    const customUrlIcon = get('url', customURL) && get('selectedIconValue.icon', customURL);
    const customURLLink = customUrlIcon && String.format('<span {1} class="{0}" />', customUrlIcon, customURLAttribute);

    const embeddedJsAttribute = `data-selectedfieldname="${encodeURI(
      JSON.stringify(fieldInfo.fieldName)
    )}" data-recordIndex="${index}" data-hyperlinkaction="true" data-isEmbeddedJavascript="true"`;

    const embeddedJavascript = get('embeddedJavascript', fieldInfoProperties);
    const embeddedJavascriptIcon =
      get('script', embeddedJavascript) && get('selectedIconValue.icon', embeddedJavascript);
    const embeddedJavascriptLink =
      embeddedJavascriptIcon && String.format('<span {1} class="{0}" />', embeddedJavascriptIcon, embeddedJsAttribute);

    return [customURLLink, embeddedJavascriptLink].join('');
  }

  applyHyperLink(text: string, fieldInfo: any, index: number) {
    const fieldInfoProperties = fieldInfo.properties;
    const subReportConfig = get('subReport', fieldInfoProperties);
    const customURL = get('customURL', fieldInfoProperties);
    const embeddedJavascript = get('embeddedJavascript', fieldInfoProperties);

    const numberOfPointActions = fieldInfo.numberOfPointActions;

    const attribute = `data-selectedfieldname="${encodeURI(
      JSON.stringify(fieldInfo.fieldName)
    )}" data-recordIndex="${index}" data-hyperlinkaction="true"`;

    let isLink = false;
    let icon;
    if (numberOfPointActions === 1) {
      subReportConfig.selectedReport &&
        (subReportConfig.viewSettingByLink ? (isLink = true) : (icon = subReportConfig.selectedIconValue.icon));
      customURL.url && (customURL.viewSettingByLink ? (isLink = true) : (icon = customURL.selectedIconValue.icon));
      embeddedJavascript.script &&
        (embeddedJavascript.viewSettingByLink ? (isLink = true) : (icon = embeddedJavascript.selectedIconValue.icon));
    } else if (numberOfPointActions > 1) {
      isLink = true;
    } else if (numberOfPointActions === 0) {
      return text;
    }

    if (isLink) {
      return `<a ${attribute} >${text}</any>`;
    }
    return `<a class="${icon}" ${attribute} ></any>${text}`;
  }

  /**
   * Validate a field by its field-name attribute.
   * @param  {Object} field
   * @param  {Object} context
   */
  validateField(field: any, context: any) {
    return ReportPartFormUtils.validateField(field, context);
  }

  /**
   * Update container field
   * @return {[type]} [description]
   */
  updateContainerField(elements: Array<any>, content: string) {
    const fields = $izenda('<div>' + content + '</div>').find('field');
    const arrayTempFields = [];

    if (fields.length === elements.length) {
      return null;
    }

    fields.each((index, item) => {
      arrayTempFields.push({
        fieldName: item.textContent
      });
    });

    return intersectionBy('fieldName', elements, arrayTempFields);
  }

  /**
   * Get subReport object for embedded ReportPart
   * @return {Object} subReportObject
   */
  getSubReportObject(
    fieldInfo: any,
    index: number,
    reportPartUsed: any,
    subReportFilter: any,
    reportPartDef: ReportPartDetails
  ): any {
    const embeddedReportPartId = ['EmbededSubReport', index, reportPartUsed, fieldInfo.position].join('_');

    return {
      id: reportPartUsed,
      reportPartDomId: embeddedReportPartId,
      queryObject: subReportFilter,
      reportPartDef,
      fieldInfo
    };
  }

  /**
   *get subReport queryObject for embedded ReportPart
   * @return {Object} [description]
   */
  getSubReportQuery(
    fieldInfo: any,
    data: any,
    previewDataFieldsMapping: Array<any>,
    reportId: string,
    reportPart: ReportPartDetails,
    overideFilterValue: any
  ) {
    const fieldInfoProperties = fieldInfo.properties;
    const subReportConfig = get('subReport', fieldInfoProperties);
    const rawRecord = [data];

    const filters = SubReportUtils.buildFilterDataCommon(rawRecord, subReportConfig.mappingFields, reportPart);

    if (!filters.success) {
      return filters;
    }
    return SubReportUtils.buildFilterDataForEmbededReportPart(
      filters.fieldMapping,
      reportPart,
      subReportConfig,
      overideFilterValue
    );
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartFormField.js